// Products Service
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  arrayUnion,
  increment,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { getMaintenanceSettings } from './admin';
import { notifyAdminsAndModerators, notifyModeratorsOnly, createNotification, NOTIFICATION_TYPES } from './notifications';
import { getUserStoreRole, hasPermission } from './storeMembers';
import { recordApprovalLog, AUDIT_ENTITY, AUDIT_ACTION } from './auditLog';
import logger from '../utils/logger';
import { MAX_DISCOUNT_PERCENT } from '../utils/pricing';

// Fiyat değişikliği eşiği: bu oran altındaki değişiklikler otomatik onaylanır
const AUTO_APPROVE_PRICE_THRESHOLD = 0.20; // %20

/**
 * Satıcının tüm ürünlerini getirir (onay durumuna göre)
 * @param {string} sellerId - Satıcı ID (userId)
 * @param {boolean} includeAll - Tüm durumları getir (admin için)
 * @returns {Promise}
 */
export const getMyProducts = async (sellerId, includeAll = false) => {
  try {
    const productsRef = collection(db, 'products');
    const q = query(
      productsRef, 
      where('sellerId', '==', sellerId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const products = [];
    
    querySnapshot.forEach((doc) => {
      products.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return { success: true, products };
  } catch (error) {
    logger.error('Get my products error:', error);
    return { success: false, error: 'Ürünler yüklenirken bir hata oluştu.' };
  }
};

/**
 * Belirli bir ürünü getirir
 * @param {string} productId - Ürün ID
 * @returns {Promise}
 */
export const getProduct = async (productId) => {
  try {
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);
    
    if (productSnap.exists()) {
      return { 
        success: true, 
        product: {
          id: productSnap.id,
          ...productSnap.data()
        }
      };
    } else {
      return { success: false, error: 'Ürün bulunamadı.' };
    }
  } catch (error) {
    logger.error('Get product error:', error);
    return { success: false, error: 'Ürün yüklenirken bir hata oluştu.' };
  }
};

/**
 * Yeni ürün ekler
 * @param {string} sellerId - Satıcı ID
 * @param {Object} productData - Ürün bilgileri
 * @returns {Promise}
 */
export const addProduct = async (sellerId, productData) => {
  try {
    const maintenance = await getMaintenanceSettings();
    if (maintenance.success && maintenance.data?.enabled) {
      return { success: false, error: 'Site şu an bakımda. Ürün eklenemiyor.' };
    }
    // Yeni ürün ID'si oluştur
    const productsRef = collection(db, 'products');
    const newProductRef = doc(productsRef);
    
    const product = {
      sellerId: sellerId,
      name: productData.name,
      description: productData.description,
      category: productData.category,
      productType: productData.productType || 'model', // 'model' veya 'drawing'
      price: parseFloat(productData.price),
      images: productData.images || [],
      thumbnails: productData.thumbnails || [],
      fileUrl: productData.fileUrl || null, // Çizim dosyası URL'i (STL, OBJ, etc.)
      specifications: productData.specifications || {},
      // 3D Model özellikleri
      filamentType: productData.filamentType || null,
      printQuality: productData.printQuality || 'normal',
      printTime: productData.printTime || null, // dakika cinsinden
      materialWeight: productData.materialWeight || null, // gram cinsinden
      supportRequired: productData.supportRequired || false,
      postProcessing: productData.postProcessing || [],
      inStock: productData.inStock !== false,
      stockCount: parseInt(productData.stockCount) || 0,
      tags: productData.tags || [],
      approvalStatus: 'pending', // Admin onayı bekliyor
      approvedBy: null,
      approvedAt: null,
      rejectionReason: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      viewCount: 0,
      salesCount: 0,
      rating: 0,
      reviewCount: 0
    };
    
    await setDoc(newProductRef, product);
    
    // Admin ve moderator'lere bildirim gönder
    await notifyAdminsAndModerators(
      NOTIFICATION_TYPES.NEW_PRODUCT_SUBMISSION,
      'Yeni Ürün Başvurusu',
      `${productData.name} adlı ürün onay bekliyor`,
      {
        link: `/admin?tab=content-approvals`,
        productId: newProductRef.id,
        sellerId: sellerId
      }
    );

    return { 
      success: true, 
      productId: newProductRef.id,
      message: 'Ürün başarıyla eklendi!' 
    };
  } catch (error) {
    logger.error('Add product error:', error);
    return { success: false, error: 'Ürün eklenirken bir hata oluştu.' };
  }
};

/**
 * Ürünü günceller.
 * - Düz satıcı: düzenleme (görsel, isim, fiyat vb.) hemen yayına alınmaz; moderator/admin onayı bekler.
 *   (Yalnız fiyat ve eşik altı ise otomatik onaylanır.)
 * - Admin: HER ürünü doğrudan (onaysız) düzenleyebilir.
 * - Moderator: KENDİ mağazasındaki ürünü doğrudan (onaysız) düzenleyebilir.
 *   Doğrudan düzenlemeler denetim kaydına (approvalLogs) yazılır.
 * @param {string} productId - Ürün ID
 * @param {string} callerUid - İşlemi yapan kullanıcının uid'i (yetkilendirme için)
 * @param {Object} productData - Güncellenecek ürün bilgileri
 * @param {string[]} [callerRoles=[]] - İşlemi yapanın global rolleri (admin/moderator/seller/user)
 * @returns {Promise}
 */
export const updateProduct = async (productId, callerUid, productData, callerRoles = []) => {
  try {
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);

    if (!productSnap.exists()) {
      return { success: false, error: 'Ürün bulunamadı.' };
    }

    const existingProduct = productSnap.data();
    const roles = Array.isArray(callerRoles) ? callerRoles : [];
    const isAdminCaller = roles.includes('admin');
    const isModeratorCaller = roles.includes('moderator');
    const isOwner = existingProduct.sellerId === callerUid;

    // Yetki: ürün sahibi, admin veya 'products.manage' yetkisine sahip aktif ekip üyesi (owner/manager).
    let canEdit = isOwner || isAdminCaller;
    if (!canEdit && callerUid) {
      const { role } = await getUserStoreRole(existingProduct.sellerId, callerUid);
      canEdit = hasPermission(role, 'products.manage');
    }
    if (!canEdit) {
      return { success: false, error: 'Bu ürünü düzenleme yetkiniz yok.' };
    }

    // NaN koruması: boş/geçersiz değer gelirse mevcut değeri koru (Firestore NaN'ı reddeder)
    const parsedPrice = productData.price != null ? parseFloat(productData.price) : existingProduct.price;
    const price = Number.isNaN(parsedPrice) ? existingProduct.price : parsedPrice;
    const parsedStock = productData.stockCount != null ? parseInt(productData.stockCount, 10) : existingProduct.stockCount;
    const stockCount = Number.isNaN(parsedStock) ? (existingProduct.stockCount ?? 0) : parsedStock;
    const oldPrice = existingProduct.price;
    const priceChangedBase = oldPrice != null && price !== oldPrice;

    // Onayı atlayabilenler: admin (her ürün) VEYA kendi mağazasındaki moderator.
    // Doğrudan canlı alanlara yazar, onay kuyruğuna girmez; denetim kaydı tutulur.
    if (isAdminCaller || (isModeratorCaller && isOwner)) {
      const directUpdate = {
        name: productData.name ?? existingProduct.name,
        description: productData.description ?? existingProduct.description,
        category: productData.category ?? existingProduct.category,
        productType: productData.productType ?? existingProduct.productType,
        images: Array.isArray(productData.images) ? productData.images : (existingProduct.images || []),
        thumbnails: Array.isArray(productData.thumbnails) ? productData.thumbnails : (existingProduct.thumbnails || []),
        fileUrl: productData.fileUrl !== undefined ? productData.fileUrl : existingProduct.fileUrl,
        price,
        stockCount,
        inStock: productData.inStock !== undefined ? productData.inStock : existingProduct.inStock,
        specifications: productData.specifications ?? existingProduct.specifications ?? {},
        filamentType: productData.filamentType ?? existingProduct.filamentType,
        printQuality: productData.printQuality ?? existingProduct.printQuality,
        printTime: productData.printTime ?? existingProduct.printTime,
        materialWeight: productData.materialWeight ?? existingProduct.materialWeight,
        supportRequired: productData.supportRequired ?? existingProduct.supportRequired,
        postProcessing: Array.isArray(productData.postProcessing) ? productData.postProcessing : (existingProduct.postProcessing || []),
        tags: Array.isArray(productData.tags) ? productData.tags : (existingProduct.tags || []),
        // Bekleyen düzenleme varsa temizle (doğrudan düzenleme onu geçersiz kılar)
        hasPendingEdit: false,
        pendingEditAt: null,
        pendingName: null, pendingDescription: null, pendingCategory: null, pendingProductType: null,
        pendingImages: null, pendingThumbnails: null, pendingFileUrl: null, pendingPrice: null,
        pendingStockCount: null, pendingInStock: null, pendingSpecifications: null,
        pendingFilamentType: null, pendingPrintQuality: null, pendingPrintTime: null,
        pendingMaterialWeight: null, pendingSupportRequired: null, pendingPostProcessing: null,
        pendingTags: null, pendingPriceHistory: null,
        productEditRejectionReason: null,
        productEditRejectionNoteVisibleToUser: null,
        lastEditedBy: callerUid,
        lastEditedRole: isAdminCaller ? 'admin' : 'moderator',
        updatedAt: serverTimestamp()
      };
      if (priceChangedBase) {
        directUpdate.priceHistory = arrayUnion({
          oldPrice,
          newPrice: price,
          date: new Date().toISOString(),
          changedBy: callerUid,
          autoApproved: true,
          directEdit: true
        });
      }

      // Admin doğrudan düzenleme, bekleyen/reddedilmiş bir ürünü de yayına alır (admin tam yetkili).
      // Moderator kendi ürününü düzenlediğinde onay durumu DEĞİŞMEZ — ilk onay başka bir yetkiliden gelmeli.
      if (isAdminCaller && existingProduct.approvalStatus !== 'approved') {
        directUpdate.approvalStatus = 'approved';
        directUpdate.approvedBy = callerUid;
        directUpdate.approvedAt = serverTimestamp();
        directUpdate.rejectionReason = null;
        directUpdate.rejectionNoteVisibleToUser = null;
      }

      await updateDoc(productRef, directUpdate);

      // Denetim kaydı (kim, ne değiştirdi — görseller dahil)
      const beforeSnapshot = {
        name: existingProduct.name, price: existingProduct.price, description: existingProduct.description,
        category: existingProduct.category, images: existingProduct.images || [], thumbnails: existingProduct.thumbnails || [],
        fileUrl: existingProduct.fileUrl ?? null, stockCount: existingProduct.stockCount ?? 0,
        inStock: existingProduct.inStock, tags: existingProduct.tags || [],
        approvalStatus: existingProduct.approvalStatus ?? null
      };
      const afterSnapshot = {
        name: directUpdate.name, price: directUpdate.price, description: directUpdate.description,
        category: directUpdate.category, images: directUpdate.images, thumbnails: directUpdate.thumbnails,
        fileUrl: directUpdate.fileUrl ?? null, stockCount: directUpdate.stockCount ?? 0,
        inStock: directUpdate.inStock, tags: directUpdate.tags,
        approvalStatus: directUpdate.approvalStatus ?? existingProduct.approvalStatus ?? null
      };
      await recordApprovalLog({
        entityType: AUDIT_ENTITY.PRODUCT,
        entityId: productId,
        action: AUDIT_ACTION.DIRECT_EDIT,
        actorId: callerUid,
        actorRoles: roles,
        sellerId: existingProduct.sellerId,
        before: beforeSnapshot,
        after: afterSnapshot,
        note: isOwner ? 'Doğrudan düzenleme (kendi mağazası)' : 'Admin doğrudan düzenleme'
      });

      // Admin başka satıcının ürününü düzenlediyse satıcıyı bilgilendir
      if (!isOwner) {
        try {
          await createNotification(
            existingProduct.sellerId,
            NOTIFICATION_TYPES.PRODUCT_EDIT_APPROVED,
            'Ürününüz güncellendi',
            `"${directUpdate.name}" adlı ürününüz yönetim tarafından güncellendi.`,
            { link: `/product/${productId}`, productId }
          );
        } catch (e) { /* ignore */ }
      }

      return {
        success: true,
        message: isOwner
          ? 'Değişiklikler kaydedildi (onay gerektirmedi).'
          : 'Ürün güncellendi (admin yetkisiyle, onaysız).'
      };
    }

    // Fiyat değişikliği oranı hesapla
    const priceChanged = oldPrice != null && price !== oldPrice;
    const priceChangeRatio = priceChanged && oldPrice > 0
      ? Math.abs(price - oldPrice) / oldPrice
      : 0;

    // Sadece fiyat değişikliği varsa ve eşik altındaysa: fiyatı direkt güncelle, diğer değişiklikler varsa pending
    const onlyPriceChanged = priceChanged &&
      (productData.name ?? existingProduct.name) === existingProduct.name &&
      (productData.description ?? existingProduct.description) === existingProduct.description &&
      (productData.category ?? existingProduct.category) === existingProduct.category;

    const autoApprovePrice = priceChanged && priceChangeRatio <= AUTO_APPROVE_PRICE_THRESHOLD;

    // Fiyat geçmişi kaydı
    const priceHistoryEntry = priceChanged ? {
      oldPrice,
      newPrice: price,
      date: new Date().toISOString(),
      autoApproved: autoApprovePrice
    } : null;

    // Sadece fiyat değişti ve eşik altında: direkt onayla
    if (onlyPriceChanged && autoApprovePrice) {
      const directUpdate = {
        price,
        updatedAt: serverTimestamp(),
        productEditRejectionReason: null,
        productEditRejectionNoteVisibleToUser: null,
      };
      if (priceHistoryEntry) {
        directUpdate.priceHistory = arrayUnion(priceHistoryEntry);
      }
      await updateDoc(productRef, directUpdate);

      return {
        success: true,
        message: `Fiyat güncellendi (${oldPrice} ₺ → ${price} ₺). Küçük değişiklik olduğu için otomatik onaylandı.`
      };
    }

    const pendingData = {
      pendingName: productData.name ?? existingProduct.name,
      pendingDescription: productData.description ?? existingProduct.description,
      pendingCategory: productData.category ?? existingProduct.category,
      pendingProductType: productData.productType ?? existingProduct.productType,
      pendingImages: Array.isArray(productData.images) ? productData.images : (existingProduct.images || []),
      pendingThumbnails: Array.isArray(productData.thumbnails) ? productData.thumbnails : (existingProduct.thumbnails || []),
      pendingFileUrl: productData.fileUrl !== undefined ? productData.fileUrl : existingProduct.fileUrl,
      pendingPrice: price,
      pendingStockCount: stockCount,
      pendingInStock: productData.inStock !== undefined ? productData.inStock : existingProduct.inStock,
      pendingSpecifications: productData.specifications ?? existingProduct.specifications ?? {},
      pendingFilamentType: productData.filamentType ?? existingProduct.filamentType,
      pendingPrintQuality: productData.printQuality ?? existingProduct.printQuality,
      pendingPrintTime: productData.printTime ?? existingProduct.printTime,
      pendingMaterialWeight: productData.materialWeight ?? existingProduct.materialWeight,
      pendingSupportRequired: productData.supportRequired ?? existingProduct.supportRequired,
      pendingPostProcessing: Array.isArray(productData.postProcessing) ? productData.postProcessing : (existingProduct.postProcessing || []),
      pendingTags: Array.isArray(productData.tags) ? productData.tags : (existingProduct.tags || []),
      hasPendingEdit: true,
      pendingEditAt: serverTimestamp(),
      productEditRejectionReason: null,
      productEditRejectionNoteVisibleToUser: null,
      updatedAt: serverTimestamp()
    };

    if (priceHistoryEntry) {
      pendingData.pendingPriceHistory = priceHistoryEntry;
    }

    await updateDoc(productRef, pendingData);

    await notifyAdminsAndModerators(
      NOTIFICATION_TYPES.PRODUCT_EDIT_REQUEST,
      'Ürün düzenleme talebi',
      `${existingProduct.name} adlı ürün satıcı tarafından güncellendi.${priceChanged && !autoApprovePrice ? ` Fiyat %${Math.round(priceChangeRatio * 100)} değişti (${oldPrice} ₺ → ${price} ₺).` : ''} Onay bekliyor.`,
      { link: '/admin?tab=content-approvals', productId, sellerId: existingProduct.sellerId }
    );

    return {
      success: true,
      message: 'Ürün güncelleme talebiniz alındı. Moderator/admin onayından sonra yayına alınacak.'
    };
  } catch (error) {
    logger.error('Update product error:', error);
    return { success: false, error: 'Ürün güncellenirken bir hata oluştu.' };
  }
};

/**
 * Ürünü siler
 * @param {string} productId - Ürün ID
 * @param {string} sellerId - Satıcı ID (yetkilendirme için)
 * @returns {Promise}
 */
export const deleteProduct = async (productId, sellerId) => {
  try {
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);
    
    // Ürün var mı kontrol et
    if (!productSnap.exists()) {
      return { success: false, error: 'Ürün bulunamadı.' };
    }
    
    // Ürün bu satıcıya ait mi kontrol et
    const existingProduct = productSnap.data();
    if (existingProduct.sellerId !== sellerId) {
      return { success: false, error: 'Bu ürünü silme yetkiniz yok.' };
    }
    
    const productName = existingProduct.name || 'Ürün';
    await deleteDoc(productRef);

    await notifyModeratorsOnly(
      NOTIFICATION_TYPES.PRODUCT_DELETED,
      'Satıcı ürün sildi',
      `${productName} adlı ürün satıcı tarafından silindi`,
      { link: '/admin?tab=models', sellerId }
    );

    return { 
      success: true, 
      message: 'Ürün başarıyla silindi!' 
    };
  } catch (error) {
    logger.error('Delete product error:', error);
    return { success: false, error: 'Ürün silinirken bir hata oluştu.' };
  }
};

/**
 * Ürüne süreli yüzdesel indirim uygular (anında, onaysız). Taban fiyata dokunmaz.
 * own: sahip/admin/products.manage(owner+manager). pool: yalnız admin.
 */
export const setProductDiscount = async (productId, callerUid, { percent, endAt } = {}, callerRoles = []) => {
  try {
    const productRef = doc(db, 'products', productId);
    const snap = await getDoc(productRef);
    if (!snap.exists()) return { success: false, error: 'Ürün bulunamadı.' };

    const existing = snap.data();
    const roles = Array.isArray(callerRoles) ? callerRoles : [];
    const isAdminCaller = roles.includes('admin');
    const isOwner = existing.sellerId === callerUid;

    // Havuz ürünü: yalnız admin
    if (existing.fulfillmentType === 'pool' && !isAdminCaller) {
      return { success: false, error: 'Havuz ürünlerinde indirimi yalnızca yönetici uygulayabilir.' };
    }

    // Yetki (own): sahip, admin veya products.manage (owner/manager)
    let canEdit = isOwner || isAdminCaller;
    if (!canEdit && callerUid) {
      const { role } = await getUserStoreRole(existing.sellerId, callerUid);
      canEdit = hasPermission(role, 'products.manage');
    }
    if (!canEdit) return { success: false, error: 'Bu üründe indirim uygulama yetkiniz yok.' };

    // Validasyon
    const p = Math.round(Number(percent));
    if (!Number.isFinite(p) || p < 1 || p > MAX_DISCOUNT_PERCENT) {
      return { success: false, error: `İndirim oranı 1 ile ${MAX_DISCOUNT_PERCENT} arasında olmalı.` };
    }
    const endDate = endAt instanceof Date ? endAt : new Date(endAt);
    if (Number.isNaN(endDate.getTime()) || endDate.getTime() <= Date.now()) {
      return { success: false, error: 'Bitiş tarihi gelecekte olmalı.' };
    }

    const setByRole = isAdminCaller ? 'admin' : (isOwner ? 'owner' : 'manager');
    await updateDoc(productRef, {
      discount: {
        percent: p,
        startAt: serverTimestamp(),
        endAt: Timestamp.fromDate(endDate),
        setBy: callerUid,
        setByRole,
        setAt: serverTimestamp()
      },
      updatedAt: serverTimestamp()
    });
    return { success: true, message: `%${p} indirim uygulandı.` };
  } catch (error) {
    logger.error('setProductDiscount error:', error);
    return { success: false, error: 'İndirim uygulanırken bir hata oluştu.' };
  }
};

/**
 * Üründeki indirimi kaldırır (discount: null). Taban fiyat aynen geri gelir.
 */
export const clearProductDiscount = async (productId, callerUid, callerRoles = []) => {
  try {
    const productRef = doc(db, 'products', productId);
    const snap = await getDoc(productRef);
    if (!snap.exists()) return { success: false, error: 'Ürün bulunamadı.' };

    const existing = snap.data();
    const roles = Array.isArray(callerRoles) ? callerRoles : [];
    const isAdminCaller = roles.includes('admin');
    const isOwner = existing.sellerId === callerUid;

    if (existing.fulfillmentType === 'pool' && !isAdminCaller) {
      return { success: false, error: 'Havuz ürünlerinde indirimi yalnızca yönetici kaldırabilir.' };
    }

    let canEdit = isOwner || isAdminCaller;
    if (!canEdit && callerUid) {
      const { role } = await getUserStoreRole(existing.sellerId, callerUid);
      canEdit = hasPermission(role, 'products.manage');
    }
    if (!canEdit) return { success: false, error: 'Bu üründe indirim kaldırma yetkiniz yok.' };

    await updateDoc(productRef, { discount: null, updatedAt: serverTimestamp() });
    return { success: true, message: 'İndirim kaldırıldı.' };
  } catch (error) {
    logger.error('clearProductDiscount error:', error);
    return { success: false, error: 'İndirim kaldırılırken bir hata oluştu.' };
  }
};

/**
 * Ürün tipleri
 */
export const PRODUCT_TYPES = [
  { value: 'model', label: 'Hazır Model (Fotoğraflı)' },
  { value: 'drawing', label: 'Çizim Dosyası (STL/OBJ)' }
];

/**
 * Ürün kategorileri listesi
 */
export const PRODUCT_CATEGORIES = [
  { value: 'figurine', label: 'Figür' },
  { value: 'decoration', label: 'Dekorasyon' },
  { value: 'toys', label: 'Oyuncak' },
  { value: 'jewelry', label: 'Takı & Aksesuar' },
  { value: 'art', label: 'Sanat' },
  { value: 'game', label: 'Oyun & Hobi' },
  { value: 'tech', label: 'Teknoloji' },
  { value: 'tools', label: 'Araç & Gereç' },
  { value: 'household', label: 'Ev Eşyası' },
  { value: 'educational', label: 'Eğitim' },
  { value: 'other', label: 'Diğer' }
];

/**
 * Onaylı ürünleri getirir (Marketplace için)
 * Sadece approvalStatus ile sorgulayıp sıralama/filtrelemeyi bellekte yapıyoruz;
 * böylece createdAt veya productType eksik ürünler de listelenir.
 * @param {string} productType - 'model' veya 'drawing' (opsiyonel)
 * @returns {Promise}
 */
export const getApprovedProducts = async (productType = null) => {
  try {
    const productsRef = collection(db, 'products');
    const q = query(
      productsRef,
      where('approvalStatus', '==', 'approved')
    );

    const querySnapshot = await getDocs(q);
    const products = [];

    querySnapshot.forEach((docSnap) => {
      const docData = docSnap.data();
      products.push({
        id: docSnap.id,
        ...docData
      });
    });

    // productType'a göre filtre (bellekte): eksik alan = 'model' kabul et
    let filtered = products;
    if (productType === 'model') {
      filtered = products.filter((p) => (String(p.productType || 'model').toLowerCase()) === 'model');
    } else if (productType === 'drawing') {
      filtered = products.filter((p) => String(p.productType || '').toLowerCase() === 'drawing');
    }

    // Satıcı hesabını kapatmışsa listeleme
    const visible = filtered.filter((p) => p.deactivatedBySeller !== true);

    // Tarihe göre sırala (createdAt yoksa en sona)
    const getCreatedAt = (p) => {
      const t = p.createdAt;
      if (!t) return 0;
      return t.toDate ? t.toDate().getTime() : new Date(t).getTime();
    };
    visible.sort((a, b) => getCreatedAt(b) - getCreatedAt(a));

    return { success: true, products: visible };
  } catch (error) {
    logger.error('Get approved products error:', error);
    return { success: false, error: 'Ürünler yüklenirken bir hata oluştu.', products: [] };
  }
};

/**
 * Öne çıkan ürünleri getirir (Ana sayfa için)
 * isFeatured: true olan onaylı ürünleri döner
 * @param {number} limitCount - Maksimum ürün sayısı
 * @returns {Promise}
 */
export const getFeaturedProducts = async (limitCount = 6) => {
  try {
    const productsRef = collection(db, 'products');
    const q = query(
      productsRef,
      where('approvalStatus', '==', 'approved'),
      where('isFeatured', '==', true)
    );

    const querySnapshot = await getDocs(q);
    const products = [];

    querySnapshot.forEach((docSnap) => {
      const docData = docSnap.data();
      if (docData.deactivatedBySeller !== true) {
        products.push({
          id: docSnap.id,
          ...docData
        });
      }
    });

    // featuredAt tarihine göre sırala (en yeni önce)
    products.sort((a, b) => {
      const tA = a.featuredAt?.toDate ? a.featuredAt.toDate().getTime() : new Date(a.featuredAt || 0).getTime();
      const tB = b.featuredAt?.toDate ? b.featuredAt.toDate().getTime() : new Date(b.featuredAt || 0).getTime();
      return tB - tA;
    });

    return { success: true, products: products.slice(0, limitCount) };
  } catch (error) {
    logger.error('Get featured products error:', error);
    return { success: false, error: 'Öne çıkan ürünler yüklenemedi.', products: [] };
  }
};

/**
 * Ürünün öne çıkarma durumunu değiştirir (Admin)
 * @param {string} productId
 * @param {boolean} featured
 * @param {string} adminId
 * @returns {Promise}
 */
export const toggleFeaturedProduct = async (productId, featured, adminId) => {
  try {
    const productRef = doc(db, 'products', productId);
    const updateData = {
      isFeatured: featured,
      featuredAt: featured ? serverTimestamp() : null,
      featuredBy: featured ? adminId : null,
      updatedAt: serverTimestamp()
    };
    await updateDoc(productRef, updateData);
    return { success: true, message: featured ? 'Ürün öne çıkarıldı.' : 'Ürün öne çıkarmadan kaldırıldı.' };
  } catch (error) {
    logger.error('Toggle featured product error:', error);
    return { success: false, error: 'İşlem sırasında bir hata oluştu.' };
  }
};

/**
 * Satıcı öne çıkartma talebi oluşturur (ücretli)
 * @param {string} productId
 * @param {string} sellerId
 * @returns {Promise}
 */
export const requestFeaturedProduct = async (productId, sellerId) => {
  try {
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);

    if (!productSnap.exists()) {
      return { success: false, error: 'Ürün bulunamadı.' };
    }

    const product = productSnap.data();
    if (product.sellerId !== sellerId) {
      return { success: false, error: 'Bu ürün size ait değil.' };
    }

    if (product.approvalStatus !== 'approved') {
      return { success: false, error: 'Sadece onaylı ürünler öne çıkarılabilir.' };
    }

    if (product.isFeatured) {
      return { success: false, error: 'Bu ürün zaten öne çıkarılmış.' };
    }

    if (product.featuredRequestPending) {
      return { success: false, error: 'Bu ürün için zaten bir öne çıkarma talebi bekliyor.' };
    }

    await updateDoc(productRef, {
      featuredRequestPending: true,
      featuredRequestAt: serverTimestamp(),
      featuredRequestBy: sellerId,
      updatedAt: serverTimestamp()
    });

    // Admin/moderatörlere bildirim
    await notifyAdminsAndModerators(
      NOTIFICATION_TYPES.NEW_PRODUCT_SUBMISSION,
      'Öne Çıkarma Talebi',
      `${product.name} ürünü için öne çıkarma talebi alındı.`,
      { link: '/admin?tab=models', productId, sellerId }
    );

    return { success: true, message: 'Öne çıkarma talebiniz alındı. Admin onayından sonra ürününüz öne çıkarılacak.' };
  } catch (error) {
    logger.error('Request featured product error:', error);
    return { success: false, error: 'Talep oluşturulurken bir hata oluştu.' };
  }
};

/**
 * Ürün görüntülenme sayacını artırır
 * @param {string} productId
 */
export const incrementViewCount = async (productId) => {
  try {
    const productRef = doc(db, 'products', productId);
    await updateDoc(productRef, {
      viewCount: increment(1)
    });
  } catch (error) {
    // Görüntülenme sayacı kritik değil, sessizce devam et
    logger.warn('Increment view count error:', error);
  }
};

/**
 * En çok görüntülenen onaylı ürünleri getirir (fallback: öne çıkan yoksa)
 * @param {number} limitCount - Maksimum ürün sayısı
 * @returns {Promise}
 */
export const getMostViewedProducts = async (limitCount = 6) => {
  try {
    const productsRef = collection(db, 'products');
    const q = query(
      productsRef,
      where('approvalStatus', '==', 'approved'),
      orderBy('viewCount', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const products = [];

    querySnapshot.forEach((docSnap) => {
      const docData = docSnap.data();
      if (docData.deactivatedBySeller !== true) {
        products.push({
          id: docSnap.id,
          ...docData
        });
      }
    });

    return { success: true, products };
  } catch (error) {
    logger.error('Get most viewed products error:', error);
    return { success: false, error: 'En çok görüntülenen ürünler yüklenemedi.', products: [] };
  }
};

